import { type ThemeOptions } from '@mui/material/styles/createTheme';
import { hexToRgba } from 'shared/data-types/string';

const OPACITY = {
  HOVER_SUBTLE: 0.05,
  HOVER_MEDIUM: 0.06,
  HOVER_TEXT: 0.08,
  HOVER_WARM: 0.1,
  HOVER_DARK: 0.12,
  BORDER_SECONDARY: 0.22,
  BORDER_PRIMARY: 0.28,
  BORDER_MEDIUM: 0.32,
  BORDER_STRONG: 0.35,
  BORDER_WARNING: 0.42,
} as const;

const defaultButtonStyleProps = {
  fontWeight: '500 !important',
  fontSize: 12,
  textTransform: 'none',
  borderRadius: 8,
  padding: '0 15px',
  border: '1px solid',
  minHeight: 32,
  boxShadow: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

const buttonStyleOverrides = {
  root: {
    fontFamily: 'inherit',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      pointerEvents: 'auto',
      cursor: 'not-allowed !important',
    },
  },
  contained: {
    ...defaultButtonStyleProps,
    border: '1px solid transparent',
    '&.Mui-disabled': {
      borderColor: '#d1d5db !important',
      pointerEvents: 'auto',
      cursor: 'not-allowed !important',
    },
  },
  outlined: {
    ...defaultButtonStyleProps,
    '&.Mui-disabled': {
      borderColor: '#d1d5db !important',
      pointerEvents: 'auto',
      cursor: 'not-allowed !important',
    },
  },
  text: {
    ...defaultButtonStyleProps,
    border: '1px solid transparent',
    padding: '0 12px',
    '&.Mui-disabled': {
      borderColor: 'transparent !important',
      pointerEvents: 'auto',
      cursor: 'not-allowed !important',
    },
  },
  sizeMedium: {
    fontWeight: '500 !important',
    fontSize: 12,
    minHeight: 32,
    padding: '0 15px',
  },
  sizeSmall: {
    fontWeight: '500 !important',
    fontSize: 11,
    minHeight: 28,
    padding: '0 10px',
    borderRadius: 6,
  },
  sizeLarge: {
    fontWeight: '500 !important',
    fontSize: 13,
    minHeight: 40,
    padding: '0 20px',
    borderRadius: 8,
  },
} as const;

export const themeOptions: ThemeOptions = {
  typography: {
    fontFamily: [
      'Manrope',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    htmlFontSize: 10,
  },
  palette: {
    primary: {
      main: '#053871',
      light: '#0a468d',
      dark: '#03254c',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#1E293B',
      light: '#334155',
      dark: '#0f172a',
      contrastText: '#FFF',
    },
    info: {
      main: '#00B8D9',
      light: '#61f3f3',
      dark: '#006c9c',
      contrastText: '#FFF',
    },
    success: {
      main: '#059669',
      light: '#34d399',
      dark: '#047857',
      contrastText: '#FFF',
    },
    warning: {
      main: '#FFAB00',
      light: '#ffd666',
      dark: '#b76e00',
      contrastText: '#FFF',
    },
    error: {
      main: '#DE4D4D',
      light: '#ffac82',
      dark: '#b71d18',
      contrastText: '#FFF',
    },
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'not-allowed !important',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        disableElevation: true,
      },
      styleOverrides: buttonStyleOverrides,
      variants: [
        /** Contained */
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            background: '#053871',
            borderColor: '#053871',
            color: '#FFF',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              background: '#0a468d',
              borderColor: '#0a468d',
              boxShadow: '0 2px 4px 0 rgba(5, 56, 113, 0.2)',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            background: '#1E293B',
            borderColor: '#1E293B',
            color: '#FFF',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              color: '#FFF',
              background: '#334155',
              borderColor: '#334155',
              boxShadow: '0 2px 4px 0 rgba(30, 41, 59, 0.2)',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'success' },
          style: {
            background: '#059669',
            borderColor: '#059669',
            color: '#FFF',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              background: '#047857',
              borderColor: '#047857',
              boxShadow: '0 2px 4px 0 rgba(5, 150, 105, 0.2)',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'info' },
          style: {
            background: '#00B8D9',
            borderColor: '#00B8D9',
            color: '#FFF',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              background: '#009bb8',
              borderColor: '#009bb8',
              boxShadow: '0 2px 4px 0 rgba(0, 184, 217, 0.2)',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            background: '#DE4D4D',
            borderColor: '#DE4D4D',
            color: '#FFF',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              background: '#c53a3a',
              borderColor: '#c53a3a',
              boxShadow: '0 2px 4px 0 rgba(222, 77, 77, 0.2)',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'warning' },
          style: {
            background: '#FFAB00',
            borderColor: '#FFAB00',
            color: '#FFF',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              background: '#e69a00',
              borderColor: '#e69a00',
              boxShadow: '0 2px 4px 0 rgba(255, 171, 0, 0.2)',
            },
          },
        },

        /** Outlined */
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            backgroundColor: '#FFF',
            color: '#053871',
            border: `1px solid ${hexToRgba('#053871', OPACITY.BORDER_PRIMARY)}`,
            '&:hover': {
              border: '1px solid #053871',
              background: hexToRgba('#053871', OPACITY.HOVER_SUBTLE),
              color: '#053871',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            backgroundColor: '#FFF',
            color: '#1E293B',
            border: `1px solid ${hexToRgba('#1E293B', OPACITY.BORDER_SECONDARY)}`,
            '&:hover': {
              border: '1px solid #1E293B',
              background: hexToRgba('#1E293B', OPACITY.HOVER_SUBTLE),
              color: '#1E293B',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            backgroundColor: '#FFF',
            color: '#059669',
            border: `1px solid ${hexToRgba('#059669', OPACITY.BORDER_MEDIUM)}`,
            '&:hover': {
              border: '1px solid #059669',
              background: hexToRgba('#059669', OPACITY.HOVER_MEDIUM),
              color: '#059669',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'info' },
          style: {
            backgroundColor: '#FFF',
            color: '#009bb8',
            border: `1px solid ${hexToRgba('#00B8D9', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              border: '1px solid #00B8D9',
              background: hexToRgba('#00B8D9', OPACITY.HOVER_MEDIUM),
              color: '#009bb8',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            backgroundColor: '#FFF',
            color: '#DE4D4D',
            border: `1px solid ${hexToRgba('#DE4D4D', OPACITY.BORDER_MEDIUM)}`,
            '&:hover': {
              border: '1px solid #DE4D4D',
              background: hexToRgba('#DE4D4D', OPACITY.HOVER_MEDIUM),
              color: '#DE4D4D',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            backgroundColor: '#FFF',
            color: '#b76e00',
            border: `1px solid ${hexToRgba('#FFAB00', OPACITY.BORDER_WARNING)}`,
            '&:hover': {
              border: '1px solid #FFAB00',
              background: hexToRgba('#FFAB00', OPACITY.HOVER_TEXT),
              color: '#b76e00',
            },
          },
        },

        /** Text */
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            border: '1px solid transparent',
            color: '#053871',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#053871',
              border: '1px solid transparent',
              background: hexToRgba('#053871', OPACITY.HOVER_MEDIUM),
            },
          },
        },
        {
          props: { variant: 'text', color: 'secondary' },
          style: {
            border: '1px solid transparent',
            color: '#1E293B',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#1E293B',
              border: '1px solid transparent',
              background: hexToRgba('#1E293B', OPACITY.HOVER_MEDIUM),
            },
          },
        },
        {
          props: { variant: 'text', color: 'info' },
          style: {
            border: '1px solid transparent',
            color: '#009bb8',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#009bb8',
              border: '1px solid transparent',
              background: hexToRgba('#00B8D9', OPACITY.HOVER_TEXT),
            },
          },
        },
        {
          props: { variant: 'text', color: 'success' },
          style: {
            border: '1px solid transparent',
            color: '#059669',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#059669',
              border: '1px solid transparent',
              background: hexToRgba('#059669', OPACITY.HOVER_TEXT),
            },
          },
        },
        {
          props: { variant: 'text', color: 'warning' },
          style: {
            border: '1px solid transparent',
            color: '#b76e00',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#b76e00',
              border: '1px solid transparent',
              background: hexToRgba('#FFAB00', OPACITY.HOVER_WARM),
            },
          },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: {
            border: '1px solid transparent',
            color: '#DE4D4D',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#DE4D4D',
              border: '1px solid transparent',
              background: hexToRgba('#DE4D4D', OPACITY.HOVER_TEXT),
            },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 6,
          color: '#64748B',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: hexToRgba('#053871', OPACITY.HOVER_MEDIUM),
            color: '#053871',
          },
        },
      },
    },
  },
};

export const darkThemeOptions: ThemeOptions = {
  ...themeOptions,
  palette: {
    ...themeOptions.palette,
    mode: 'dark',
  },
  components: {
    ...themeOptions.components,
    MuiButton: {
      ...themeOptions.components?.MuiButton,
      styleOverrides: {
        ...buttonStyleOverrides,
        contained: {
          ...buttonStyleOverrides.contained,
          '&.Mui-disabled': {
            borderColor: '#475569 !important',
            pointerEvents: 'auto',
            cursor: 'not-allowed !important',
          },
        },
        outlined: {
          ...buttonStyleOverrides.outlined,
          '&.Mui-disabled': {
            borderColor: '#475569 !important',
            pointerEvents: 'auto',
            cursor: 'not-allowed !important',
          },
        },
      },
      variants: [
        /** Contained */
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            background: '#195394',
            border: '1px solid #195394',
            color: '#FFF',
            '&:hover': {
              background: '#2065b5',
              border: '1px solid #2065b5',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            background: '#334155',
            border: '1px solid #334155',
            color: '#FFF',
            '&:hover': {
              background: '#475569',
              border: '1px solid #475569',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'success' },
          style: {
            background: '#059669',
            border: '1px solid #059669',
            color: '#FFF',
            '&:hover': {
              background: '#10b981',
              border: '1px solid #10b981',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'info' },
          style: {
            background: '#00B8D9',
            border: '1px solid #00B8D9',
            color: '#FFF',
            '&:hover': {
              background: '#06d6fa',
              border: '1px solid #06d6fa',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'warning' },
          style: {
            background: '#d97706',
            border: '1px solid #d97706',
            color: '#FFF',
            '&:hover': {
              background: '#f59e0b',
              border: '1px solid #f59e0b',
            },
          },
        },
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            background: '#dc2626',
            border: '1px solid #dc2626',
            color: '#FFF',
            '&:hover': {
              background: '#ef4444',
              border: '1px solid #ef4444',
            },
          },
        },

        /** Outlined */
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            backgroundColor: 'transparent',
            color: '#60a5fa',
            border: `1px solid ${hexToRgba('#60a5fa', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              backgroundColor: hexToRgba('#60a5fa', OPACITY.HOVER_DARK),
              border: '1px solid #60a5fa',
              color: '#93c5fd',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            backgroundColor: 'transparent',
            color: '#cbd5e1',
            border: `1px solid ${hexToRgba('#94a3b8', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              backgroundColor: hexToRgba('#94a3b8', OPACITY.HOVER_DARK),
              border: '1px solid #94a3b8',
              color: '#f8fafc',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            backgroundColor: 'transparent',
            color: '#34d399',
            border: `1px solid ${hexToRgba('#34d399', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              backgroundColor: hexToRgba('#34d399', OPACITY.HOVER_DARK),
              border: '1px solid #34d399',
              color: '#6ee7b7',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'info' },
          style: {
            backgroundColor: 'transparent',
            color: '#38bdf8',
            border: `1px solid ${hexToRgba('#38bdf8', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              backgroundColor: hexToRgba('#38bdf8', OPACITY.HOVER_DARK),
              border: '1px solid #38bdf8',
              color: '#7dd3fc',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            backgroundColor: 'transparent',
            color: '#f87171',
            border: `1px solid ${hexToRgba('#f87171', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              backgroundColor: hexToRgba('#f87171', OPACITY.HOVER_DARK),
              border: '1px solid #f87171',
              color: '#fca5a5',
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            backgroundColor: 'transparent',
            color: '#fbbf24',
            border: `1px solid ${hexToRgba('#fbbf24', OPACITY.BORDER_STRONG)}`,
            '&:hover': {
              backgroundColor: hexToRgba('#fbbf24', OPACITY.HOVER_DARK),
              border: '1px solid #fbbf24',
              color: '#fde68a',
            },
          },
        },

        /** Text */
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#60a5fa',
            '&:hover': {
              backgroundColor: hexToRgba('#60a5fa', OPACITY.HOVER_DARK),
              color: '#93c5fd',
              border: '1px solid transparent',
            },
          },
        },
        {
          props: { variant: 'text', color: 'secondary' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#cbd5e1',
            '&:hover': {
              backgroundColor: hexToRgba('#94a3b8', OPACITY.HOVER_DARK),
              color: '#f8fafc',
              border: '1px solid transparent',
            },
          },
        },
        {
          props: { variant: 'text', color: 'success' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#34d399',
            '&:hover': {
              backgroundColor: hexToRgba('#34d399', OPACITY.HOVER_DARK),
              color: '#6ee7b7',
              border: '1px solid transparent',
            },
          },
        },
        {
          props: { variant: 'text', color: 'info' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#38bdf8',
            '&:hover': {
              backgroundColor: hexToRgba('#38bdf8', OPACITY.HOVER_DARK),
              color: '#7dd3fc',
              border: '1px solid transparent',
            },
          },
        },
        {
          props: { variant: 'text', color: 'warning' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#fbbf24',
            '&:hover': {
              backgroundColor: hexToRgba('#fbbf24', OPACITY.HOVER_DARK),
              color: '#fde68a',
              border: '1px solid transparent',
            },
          },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#f87171',
            '&:hover': {
              backgroundColor: hexToRgba('#f87171', OPACITY.HOVER_DARK),
              color: '#fca5a5',
              border: '1px solid transparent',
            },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 6,
          color: '#94a3b8',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#f8fafc',
          },
        },
      },
    },
  },
};
